// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Box, Stack, useMediaQuery, useTheme, IconButton, TextField, Checkbox, Fade } from "@mui/material";
import {
  GridColDef,
  GridRowId,
  GridRenderCellParams,
  GridActionsCellItem,
  GridValueFormatterParams,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { DeleteConfirmModal, Table, createBooleanOperator } from "../../molecules";
import { Loading, StatusBar } from "../../atoms";
import {
  DeleteCommissionParameterRequest,
  ICommissionParameter,
  useDeleteCommissionParameter,
  useGetCommissionParameterList,
  PagingResponse,
  useAuthorization,
  useGetMerchantStatusList,
  useUpdateCommissionParameterProperty,
} from "../../../hooks";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { useNavigate } from "react-router-dom";
import { downloadExcel } from "../../../util/downloadExcel";
import dayjs from "dayjs";
import { createStatusOperator } from "../../molecules/StatusFilterComponents";
import { RequestFilterValueTypeEnums } from "../../../common/enums/RequestFilterEnums";

function RenderStatus(props: GridRenderCellParams<any, string>) {
  const { value } = props;

  return <>{value && <StatusBar status={value} />}</>;
}

function RenderCheckBar(props: GridRenderCellParams<any, string>) {
  const { value } = props;

  return value ? (
    <CheckCircleOutlineIcon style={{ color: "#00B0A6" }} />
  ) : (
    <CloseIcon style={{ color: "#F5004A" }} />
  );
}

const CustomEditComponent = (props: GridRenderCellParams & {
  updateProperty: ReturnType<typeof useUpdateCommissionParameterProperty>['mutate'];
  setSnackbar: ReturnType<typeof useSetSnackBar>;
  tableData: PagingResponse<Array<ICommissionParameter>> | undefined;
  setTableData: React.Dispatch<React.SetStateAction<PagingResponse<Array<ICommissionParameter>> | undefined>>;
  propertyNameMapping: { [key: string]: string };
}) => {
  const { id, field, value: initialValue, api, colDef, updateProperty, setSnackbar, tableData, setTableData, propertyNameMapping } = props;
  const [value, setValue] = useState(initialValue);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const buttonsContainerRef = React.useRef<HTMLDivElement>(null); // New ref for buttons container

  const [rect, setRect] = useState<DOMRect | null>(null);

  React.useEffect(() => {
    const calculateRect = () => {
      if (anchorRef.current) {
        setRect(anchorRef.current.getBoundingClientRect());
      }
    };

    calculateRect(); // Initial calculation

    const handleScrollResize = () => {
      calculateRect();
    };

    window.addEventListener('scroll', handleScrollResize, true);
    window.addEventListener('resize', handleScrollResize);

    // Also need to handle scroll within the DataGrid itself
    const gridRoot = anchorRef.current?.closest('.MuiDataGrid-root');
    const virtualScroller = gridRoot?.querySelector('.MuiDataGrid-virtualScroller');
    if (virtualScroller) {
      virtualScroller.addEventListener('scroll', handleScrollResize);
    }

    return () => {
      window.removeEventListener('scroll', handleScrollResize, true);
      window.removeEventListener('resize', handleScrollResize);
      if (virtualScroller) {
        virtualScroller.removeEventListener('scroll', handleScrollResize);
      }
    };
  }, []);

  const handleSave = () => {
    console.log("CustomEditComponent: handleSave called. Stopping cell edit mode.");

    // Get the current value directly from the DataGrid's state to avoid stale closure issues
    const currentGridValue = api.getCellValue(id, field);
    const oldValue = currentGridValue; // Use this for comparison

    const isNumeric = colDef.type === 'number' || colDef.type === 'decimal' || colDef.type === 'decimalPercentage';

    let frontendValue: string | number | boolean | null = value;
    let backendValue = String(value);

    if (isNumeric) {
      const valueAsString = String(value);
      // For frontend state, always use a dot and convert to a number
      frontendValue = Number(valueAsString.replace(',', '.'));
      // For backend, always send a comma
      backendValue = valueAsString.replace('.', ',');

      if (isNaN(frontendValue)) {
        setSnackbar({
          severity: "error",
          description: "Geçersiz sayı formatı.",
          isOpen: true,
        });
        api.stopCellEditMode({ id, field, ignoreModifications: true });
        return;
      }
    }


    if (frontendValue !== oldValue) { // Compare with the most up-to-date value
      console.log("Value changed. Preparing payload from CustomEditComponent...");
      const backendPropertyName = propertyNameMapping[field] || field; // Use mapped name, or fallback
      const payload = [
        {
          id: id as number,
          propertyName: backendPropertyName,
          propertyValue: backendValue, // Ensure value is always a string
        },
      ];
      console.log("Payload from CustomEditComponent:", payload);

      updateProperty(payload, {
        onSuccess: (data) => {
          console.log("Update successful from CustomEditComponent:", data);
          if (data.isSuccess) {
            setSnackbar({
              severity: "success",
              description: data.message || "Güncelleme başarılı!",
              isOpen: true,
            });

            // Local tableData update
            setTableData(prevTableData => {
              if (!prevTableData || !prevTableData.result) return prevTableData;

              const updatedResult = prevTableData.result.map(row => {
                if (row.id === id) {
                  return { ...row, [field]: frontendValue }; // Update local state with frontend field name
                }
                return row;
              });
              return { ...prevTableData, result: updatedResult };
            });

          } else {
            setSnackbar({
              severity: "error",
              description: data.message || "Güncelleme başarısız.",
              isOpen: true,
            });
          }
        },
        onError: (error) => {
          console.error("Update failed from CustomEditComponent:", error);
          setSnackbar({
            severity: "error",
            description: "İşlem sırasında bir hata oluştu.",
            isOpen: true,
          });
        },
      });
    } else {
      console.log("Value did not change in CustomEditComponent. No API call made.");
    }

    api.setEditCellValue({ id, field, value: frontendValue });
    api.stopCellEditMode({ id, field, ignoreModifications: false });
  };

  const handleCancel = () => {
    console.log("CustomEditComponent: handleCancel called. Stopping cell edit mode.");
    api.stopCellEditMode({ id, field, ignoreModifications: true });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  const getInputComponent = () => {
    if (colDef.type === 'boolean') {
      return (
        <Checkbox
          ref={inputRef}
          checked={!!value}
          onChange={(e) => setValue(e.target.checked)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      );
    }
    return (
      <TextField
        value={value === null || value === undefined ? '' : value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        variant="standard"
        fullWidth
        autoFocus
        InputProps={{
          disableUnderline: true,
          style: { height: '100%', padding: '0 10px' }
        }}
        sx={{
          '& .MuiInputBase-root': {
            height: '100%',
          }
        }}
      />
    );
  };

  const Overlay = (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 1300,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(1px)',
    }} />
  );

  const PortaledInput = rect ? (
    <div style={{
      position: 'fixed',
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      zIndex: 1301,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      {getInputComponent()}
    </div>
  ) : null;

  const Buttons = rect ? (
    <Fade in={!!rect} mountOnEnter unmountOnExit>
      <Box
        ref={buttonsContainerRef} // Assign ref to the Box (though not strictly needed for Fade, keeping for consistency/future)
        sx={{
          position: 'fixed',
          top: rect.top - 38,
          left: rect.right - 74,
          zIndex: 1301,
          display: 'flex',
        }}
      >
        <Box
          sx={{
            background: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            marginRight: '4px',
          }}
        >
          <IconButton onClick={handleSave} size="small" title="Save">
            <CheckIcon style={{ color: 'green' }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            background: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <IconButton onClick={handleCancel} size="small" title="Cancel">
            <CloseIcon style={{ color: 'red' }} />
          </IconButton>
        </Box>
      </Box>
    </Fade>
  ) : null;

  return (
    <div ref={anchorRef} style={{ width: '100%', height: '100%' }}>
      {/* Cell content is now empty, just an anchor */}
      {ReactDOM.createPortal(Overlay, document.body)}
      {ReactDOM.createPortal(PortaledInput, document.body)}
      {ReactDOM.createPortal(Buttons, document.body)}
    </div>
  );
};

type BankCommissionListingTableProps = {
  onRowClick?: (data: { id: GridRowId; row: ICommissionParameter }) => void;
};

export const BankCommissionListingTable = ({
  onRowClick,
}: BankCommissionListingTableProps) => {
  const theme = useTheme();
  const { showDelete, showUpdate } = useAuthorization();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [queryOptions, setQueryOptions] = React.useState({});
  const [tableData, setTableData] =
    useState<PagingResponse<Array<ICommissionParameter>>>();
  const [editingRowId, setEditingRowId] = useState<GridRowId | null>(null);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  });
  const { mutate: getCommissionParameterList, isLoading } =
    useGetCommissionParameterList();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(0);
  const setSnackbar = useSetSnackBar();
  const { mutate: commissionParameterDelete, isLoading: isDeleteLoading } =
    useDeleteCommissionParameter();
  const { data: rawMerchantStatus, isLoading: isMerchantStatusLoading } =
    useGetMerchantStatusList({});
  const { mutate: updateProperty, isLoading: isUpdating } = useUpdateCommissionParameterProperty();


  useEffect(() => {
    const savedFilter = localStorage.getItem("bankCommissionCodeFilter");
    if (savedFilter) {
      setQueryOptions(JSON.parse(savedFilter));
    }
  }, []);



  const merchantStatuses = useMemo(() => {
    return rawMerchantStatus?.data?.map(
      (merchantStat: { value: string; key: number }) => {
        return {
          label: merchantStat.key,
          value: `${merchantStat.value}`,
        };
      }
    );
  }, [rawMerchantStatus?.data]);

  useEffect(() => {
    const requestPayload = {
      size: paginationModel.pageSize,
      page: paginationModel.page,
      orderBy: "CreateDate",
      orderByDesc: true,
      ...getQueryParams(queryOptions),
    };
    if (queryOptions?.field && queryOptions?.value !== undefined) {
      requestPayload[queryOptions.field] = queryOptions.value;
    }

    getCommissionParameterList(
      requestPayload,
      {
        onSuccess: (data) => {
          if (data.isSuccess) {
            setTableData(data?.data);
          } else {
            setSnackbar({
              severity: "error",
              description: data.message,
              isOpen: true,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: "error",
            description: "İşlem sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      }
    );
  }, [
    getCommissionParameterList,
    paginationModel.page,
    paginationModel.pageSize,
    setSnackbar,
    queryOptions
  ]);

  const handleChangePagination = (model: GridPaginationModel) => {
    setPaginationModel(model);
    setTableData(undefined);

    getCommissionParameterList(
      {
        size: paginationModel.pageSize,
        page: paginationModel.page,
        orderBy: "CreateDate",
        orderByDesc: true,
      },
      {
        onSuccess: (data) => {
          if (data.isSuccess) {
            setTableData(data?.data);
          } else {
            setSnackbar({
              severity: "error",
              description: data.message,
              isOpen: true,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: "error",
            description: "İşlem sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      }
    );
  };

  const editRow = React.useCallback(
    (commissionParameter: ICommissionParameter) => () => {
      navigate("/commission-management/commission-definition", {
        state: commissionParameter,
      });
    },
    [navigate]
  );

  const deleteRow = React.useCallback(
    (commission: any) => () => {
      setIsDeleteModalOpen(true);
      setSelectedRowId(Number(commission.id));
    },
    []
  );

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    const deleteRequest: DeleteCommissionParameterRequest = {
      id: selectedRowId,
    };

    commissionParameterDelete(deleteRequest, {
      onSuccess: (data: any) => {
        if (data.isSuccess) {
          const requestPayload = {
            size: paginationModel.pageSize,
            page: paginationModel.page,
            orderBy: "CreateDate",
            orderByDesc: true,
            ...getQueryParams(queryOptions),
          };
          if (queryOptions?.field && queryOptions?.value !== undefined) {
            requestPayload[queryOptions.field] = queryOptions.value;
          }

          getCommissionParameterList(requestPayload, {
            onSuccess: (refreshData) => {
              if (refreshData.isSuccess) {
                setTableData(refreshData?.data);
              }
            },
          });

          setSnackbar({
            severity: "success",
            isOpen: true,
            description: data.message,
          });
        } else {
          setSnackbar({
            severity: "error",
            description: data.message,
            isOpen: true,
          });
        }
      },
      onError: () => {
        setSnackbar({
          severity: "error",
          description: "İşlem sırasında bir hata oluştu",
          isOpen: true,
        });
      },
    });

    handleCloseDeleteModal();
  };
  const booleanOptions = [
    { label: "Evet", value: true },
    { label: "Hayır", value: "false" },
  ];

  const propertyNameMapping: { [key: string]: string } = {
    "onus": "Onus",
    "international": "International",
    "amex": "Amex",
    "installment": "Installment",
    "bankblocked": "Bankblocked",
    "bankblockedday": "Bankblockedday",
    "bankcommission": "Bankcommission",
    "merchantblocked": "Merchantblocked",
    "merchantblockedday": "Merchantblockedday",
    "merchantcommission": "Merchantcommission",
    "merchantadditionalcommission": "Merchantadditionalcommission",
    "minAmount": "MinAmount",
    "maxAmount": "MaxAmount",
    "commissionFlag": "CommissionFlag",
    "softPOSCommission": "SoftPOSCommission",
    "integratorCommissionRate": "IntegratorCommissionRate",
    "integratorAdditionalCommision": "IntegratorAdditionalCommision",
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "Aksiyonlar",
        type: "actions",
        width: 80,
        getActions: (params) => [
          !!showUpdate ? <GridActionsCellItem
            onClick={editRow(params.row)}
            label="Düzenle"
            showInMenu
          /> : <></>,
          !!showDelete ? <GridActionsCellItem
            label="Sil"
            onClick={deleteRow(params.row)}
            showInMenu
          /> : <></>,
        ],
      },
      { field: "profileCode", headerName: "Profil", width: 200 },
      { field: "merchantId", headerName: "Üye İşyeri Kodu", width: 150 },
      { field: "merchantName", headerName: "Üye İşyeri Adı", width: 380 },
      { field: "txnType", headerName: "İşlem Tipi", width: 200 },
      {
        field: "onus",
        headerName: "Onus",
        width: 100,
        renderCell: RenderCheckBar,
        filterOperators: [createBooleanOperator(booleanOptions)],
        editable: true,
        type: 'boolean',
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        ),
      },
      {
        field: "international",
        headerName: "International",
        width: 130,
        renderCell: RenderCheckBar,
        filterOperators: [createBooleanOperator(booleanOptions)],
        editable: true,
        type: 'boolean',
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        ),
      },
      {
        field: "amex",
        headerName: "Yi Amex",
        width: 100,
        renderCell: RenderCheckBar,
        filterOperators: [createBooleanOperator(booleanOptions)],
        editable: true,
        type: 'boolean',
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        ),
      },
      {
        field: "installment", headerName: "Taksit Sayısı", width: 150, editable: true, type: 'number', renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        )
      },
      { field: "bankcode", headerName: "Banka Kodu", width: 150 },
      { field: "bankName", headerName: "Banka Adı", width: 350 },
      {
        field: "commissionFlag", headerName: "Komisyonu Son Kullanıcıya Yansıt", width: 150,
        valueFormatter: (params) => (params.value ? "Evet" : "Hayır"), filterOperators: [createBooleanOperator(booleanOptions)],
        editable: true,
        type: 'boolean',
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        ),
      },
      {
        field: "bankblocked", headerName: "Banka Blokesi", width: 150,
        valueFormatter: (params) => (params.value ? "Evet" : "Hayır"), filterOperators: [createBooleanOperator(booleanOptions)],
        editable: true,
        type: 'boolean',
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        ),
      },
      {
        field: "bankblockedday", headerName: "Banka Bloke Gün", width: 150, editable: true, type: 'number', renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        )
      },
      {
        field: "bankcommission",
        headerName: "Banka Komisyon",
        width: 150,
        type: "decimalPercentage",
        editable: true,
        // The actual type for editing should be 'number' for the input field
        // The valueFormatter handles the display.
        valueFormatter: (params: GridValueFormatterParams<number>) => {
          if (params.value == null) {
            return "";
          }
          return `${params.value} %`;
        },
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        )
      },
      {
        field: "integratorCommissionRate", headerName: "Entegratör Komisyon Oranı", width: 150,
        columnType: RequestFilterValueTypeEnums.NUMBER,
        type: 'decimalPercentage',
        editable: true,
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        )
      },
      {
        field: "integratorAdditionalCommision", headerName: "Entegratör Ek Komisyon Tutarı", width: 150,
        type: "decimal",
        editable: true,
        valueFormatter: (params: GridValueFormatterParams<number>) => {
          if (params.value == null) {
            return "";
          }
          return `${params.value} TL`;
        }, columnType: RequestFilterValueTypeEnums.NUMBER,
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        )
      },
      {
        field: "merchantblocked", headerName: "İşyeri Blokesi", width: 150, valueFormatter: (params) => (params.value ? "Evet" : "Hayır"), filterOperators: [createBooleanOperator(booleanOptions)],
        editable: true,
        type: 'boolean',
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        ),
      },
      {
        field: "merchantblockedday",
        headerName: "İşyeri Bloke Gün",
        width: 150,
        editable: true,
        type: 'number',
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        )
      },
      {
        field: "merchantcommission",
        headerName: "İşyeri Komisyon",
        width: 150,
        type: "decimalPercentage",
        editable: true,
        valueFormatter: (params: GridValueFormatterParams<number>) => {
          if (params.value == null) {
            return "";
          }
          return `${params.value} %`;
        },
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        )
      },
      {
        field: "merchantadditionalcommission",
        headerName: "Üye İşyeri Ücreti",
        width: 150,
        type: 'decimal',
        editable: true,
        valueFormatter: (params: GridValueFormatterParams<number>) => {
          if (params.value == null) {
            return "";
          }
          return `${params.value} TL`;
        },
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        )
      },
      {
        field: "softPOSCommission",
        headerName: "SoftPOS Komisyon Oranı",
        width: 150,
        type: "decimalPercentage",
        editable: true,
        valueFormatter: (params: GridValueFormatterParams<number>) => {
          if (params.value == null) {
            return "";
          }
          return `${params.value} %`;
        },
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        )
      },
      {
        field: "brandName",
        headerName: "Kart Markası",
        width: 150
      },
      { field: "cardType", headerName: "Kart Tipi", width: 150 },
      {
        field: "minAmount",
        headerName: "En Düşük Tutar",
        type: 'decimal',
        width: 200,
        editable: true,
        valueFormatter: (params: GridValueFormatterParams<number>) => {
          if (params.value == null) {
            return "";
          }
          return `${params.value} TL`;
        },
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        )
      },
      {
        field: "maxAmount",
        headerName: "En Yüksek Tutar",
        width: 200,
        type: 'decimal',
        editable: true,
        valueFormatter: (params: GridValueFormatterParams<number>) => {
          if (params.value == null) {
            return "";
          }
          return `${params.value} TL`;
        },
        renderEditCell: (params) => (
          <CustomEditComponent
            {...params}
            updateProperty={updateProperty}
            setSnackbar={setSnackbar}
            tableData={tableData}
            setTableData={setTableData}
            propertyNameMapping={propertyNameMapping}
          />
        )
      },
      {
        renderCell: RenderStatus,
        field: "status",
        headerName: "Durum",
        width: 150,
        filterOperators: [createStatusOperator(merchantStatuses)],

      },
      {
        field: "systemDate", headerName: "Düzenlenme Tarihi", width: 200,
        renderCell: (params) => {
          if (!params.value) return ""; // Eğer değer null veya undefined ise boş döndür
          const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
          if (formattedDate === "Invalid Date") return ""; // Eğer dönüşüm sonucu "Invalid Date" ise boş döndür
          return formattedDate;
        }
      },
      {
        field: "createUserName",
        headerName: "Düzenleyen Kullanıcı",
        width: 200,
      },
      {
        field: "updateDate", headerName: "Güncellenme Tarihi", width: 200,
        renderCell: (params) => {
          if (!params.value) return ""; // Eğer değer null veya undefined ise boş döndür
          const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
          if (formattedDate === "Invalid Date") return ""; // Eğer dönüşüm sonucu "Invalid Date" ise boş döndür
          return formattedDate;
        }
      },
      {
        field: "updateUserName",
        headerName: "Güncelleyen Kullanıcı",
        width: 200,
      },
    ];
  }, [deleteRow, editRow, showDelete, showUpdate, tableData, updateProperty, setSnackbar, setTableData, propertyNameMapping]);
  const keyMapping = {
    "status": "Durum",
  };
  const onSave = () => {
    const requestPayload = {
      size: -1,
      page: 0,
      orderBy: "CreateDate",
      orderByDesc: true,
      ...getQueryParams(queryOptions),
    };

    if (queryOptions?.field && queryOptions?.value !== undefined) {
      requestPayload[queryOptions.field] = queryOptions.value;
    }
    getCommissionParameterList(
      requestPayload,
      {
        onSuccess: (data) => {
          if (data.isSuccess) {
            function fieldValue(data) {
              return data.map((item) => {
                return {
                  "Profil": item?.profileCode,
                  "Üye İşyeri Kodu": item?.merchantId,
                  "Üye İşyeri Adı": item?.merchantName,
                  "İşlem Tipi": item?.txnType,
                  "Onus": item?.onus ? "Evet" : "Hayır",
                  "International": item?.international ? "Evet" : "Hayır",
                  "Yi Amex": item?.amex ? "Evet" : "Hayır",
                  "Taksit Sayısı": item?.installment,
                  "Banka Kodu": item?.bankcode,
                  "Banka Adı": item?.bankName,
                  "Komisyonu Son Kullanıcıya Yansıt": item?.commissionFlag ? "Evet" : "Hayır",
                  "Banka Blokesi": item?.bankblocked ? "Evet" : "Hayır",
                  "Banka Bloke Gün": item?.bankblockedday,
                  "Banka Komisyon": item?.bankcommission,
                  "İşyeri Blokesi": item?.merchantblocked ? "Evet" : "Hayır",
                  "İşyeri Bloke Gün": item?.merchantblockedday,
                  "İşyeri Komisyon": item?.merchantcommission,
                  "Üye İşyeri Ücreti": item?.merchantadditionalcommission,
                  "SoftPOS Komisyon Oranı": item?.softPOSCommission,
                  "Entegratör Komisyon Oranı": item?.integratorCommissionRate,
                  "Entegratör Komisyon Tutarı": item?.integratorCommissionAmount,
                  "Kart Markası": item?.brandName,
                  "Kart Tipi": item?.cardType,
                  "En Düşük Tutar": item?.minAmount,
                  "En Yüksek Tutar": item?.maxAmount,
                  [keyMapping["status"]]: item?.status,
                  "Düzenlenme Tarihi": item?.systemDate,
                  "Düzenleyen Kullanıcı": item?.createUserName,
                  "Güncellenme Tarihi": item?.updateDate,
                  "Güncelleyen Kullanıcı": item?.updateUserName

                };
              });
            }
            const refactoredData = fieldValue(data?.data?.result);
            downloadExcel(refactoredData || [], "Banka Komisyon Listesi", keyMapping);
          } else {
            setSnackbar({
              severity: "error",
              description: data.message,
              isOpen: true,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: "error",
            description: "İşlem sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      }
    );
  };
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const getQueryParams = (filters) => {
    const queryParams = {};

    for (let key in filters) {
      let value = filters[key];
      // Eğer gelen değer string "false" ise onu boolean false'a çevir
      if (value === "false") {
        queryParams[key] = false;
      } else {
        if (value !== null && value !== undefined && value.toString().length > 0) {
          queryParams[key] = value;
        }
      }
    }
    return queryParams;
  };

  const handleFilterChange = debounce((filters) => {
    if (filters.clearFilter) {
      localStorage.removeItem('bankCommissionCodeFilter')
      return setQueryOptions({});
    }

    const queryParams = getQueryParams(filters);

    const isValueValid = (key, value) => {
      if (key === 'installment' || key === 'cardType' || key === 'softPOSCommission') { // Özel alan kontrolü
        return value?.toString().length >= 1;
      } else {
        return value?.toString().length >= 3;
      }
    }


    const allValuesValid = Object.entries(queryParams).every(([key, value]) => isValueValid(key, value));

    const previousKeysLength = Object.keys(previousQueryParams.current).length;
    const currentKeysLength = Object.keys(queryParams).length;

    // Eğer queryParams bir önceki ile aynıysa işlem yapma
    if (JSON.stringify(previousQueryParams.current) === JSON.stringify(queryParams)) {
      return;
    }

    if (allValuesValid || (previousKeysLength > currentKeysLength)) {
      setQueryOptions(queryParams);
      localStorage.setItem('bankCommissionCodeFilter', JSON.stringify(queryParams));
    }

    // Son olarak, mevcut queryParams değerini bir önceki değer olarak set edin
    previousQueryParams.current = queryParams;

  }, 500);
  const hasActiveFilters = Object?.keys(queryOptions)?.length > 0;
  // Komponent içerisinde
  const previousQueryParams = React.useRef({});



  const hasLoading = isLoading || isDeleteLoading || isUpdating;
  return (
    <>
      {hasLoading && <Loading />}
      <Stack flex={1} p={2} justifyContent="space-between">

        <>
          <Table
            columnVisibilityConfigKey="bankCommissionCodeColumnVisibilityKey"
            filterKey="bankCommissionCodeFilter"
            hasActiveFilters={hasActiveFilters}
            handleFilterChange={handleFilterChange}
            paginationModel={paginationModel}
            onPaginationModelChange={handleChangePagination}
            paginationMode="server"
            rowCount={tableData?.totalItems || 0}
            sx={{ width: isDesktop ? 1308 : window.innerWidth - 50 }}
            onRowClick={onRowClick}
            isRowSelectable={() => false}
            // disableColumnMenu
            rows={tableData?.result || []}
            columns={columns}
            exportFileName="Banka Komisyon Listesi"
            getRowClassName={(params) => {
              if (params?.row?.status === 'PASSIVE') return 'pasive-row';
              if (params?.row?.status === 'BLOCKED') return 'blocked-row';
              return '';
            }}
            onSave={() => onSave()}
            editMode='cell'
            isCellEditable={(params) => true}
          />
          <DeleteConfirmModal
            isOpen={isDeleteModalOpen}
            onClose={handleCloseDeleteModal}
            onConfirm={() => handleDeleteConfirm()}
          ></DeleteConfirmModal>
        </>

      </Stack>
    </>
  );
};
